import { Injectable, OnDestroy } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthState } from 'app/core/auth/auth.types';
import { Message } from 'app/layout/common/messages/messages.types';

@Injectable({ providedIn: 'root' })
export class SocketService implements OnDestroy {
    private _socket: Socket | null = null;
    private _authSub: Subscription;

    // Connection state
    private _connected$ = new BehaviorSubject<boolean>(false);
    get connected$() { return this._connected$.asObservable(); }

    // Notification events (THIS PLAN)
    private _notification$ = new Subject<Message>();
    get notification$() { return this._notification$.asObservable(); }

    // Future event streams (exposed now, consumed later)
    private _stageProgress$ = new Subject<any>();
    get stageProgress$() { return this._stageProgress$.asObservable(); }

    private _stageStatus$ = new Subject<any>();
    get stageStatus$() { return this._stageStatus$.asObservable(); }

    private _navUpdate$ = new Subject<any>();
    get navUpdate$() { return this._navUpdate$.asObservable(); }

    // Ideas streaming events
    private _ideaGenerated$ = new Subject<any>();
    get ideaGenerated$() { return this._ideaGenerated$.asObservable(); }

    private _ideaAssessed$ = new Subject<any>();
    get ideaAssessed$() { return this._ideaAssessed$.asObservable(); }

    private _ideaRejected$ = new Subject<any>();
    get ideaRejected$() { return this._ideaRejected$.asObservable(); }

    private _ideasBatchComplete$ = new Subject<any>();
    get ideasBatchComplete$() { return this._ideasBatchComplete$.asObservable(); }

    // Ideas property-level streaming events
    private _ideaStreamingStart$ = new Subject<any>();
    get ideaStreamingStart$() { return this._ideaStreamingStart$.asObservable(); }

    private _ideaStreamingUpdate$ = new Subject<any>();
    get ideaStreamingUpdate$() { return this._ideaStreamingUpdate$.asObservable(); }

    private _ideaStreamingComplete$ = new Subject<any>();
    get ideaStreamingComplete$() { return this._ideaStreamingComplete$.asObservable(); }

    // Deconstruction streaming events
    private _deconClaimStart$ = new Subject<any>();
    get deconClaimStart$() { return this._deconClaimStart$.asObservable(); }

    private _deconElementStart$ = new Subject<any>();
    get deconElementStart$() { return this._deconElementStart$.asObservable(); }

    private _deconElementId$ = new Subject<any>();
    get deconElementId$() { return this._deconElementId$.asObservable(); }

    private _deconElementText$ = new Subject<any>();
    get deconElementText$() { return this._deconElementText$.asObservable(); }

    private _deconElementComplete$ = new Subject<any>();
    get deconElementComplete$() { return this._deconElementComplete$.asObservable(); }

    private _deconClaimComplete$ = new Subject<any>();
    get deconClaimComplete$() { return this._deconClaimComplete$.asObservable(); }

    // Prior art search events
    private _priorArtSubstage$ = new Subject<any>();
    get priorArtSubstage$() { return this._priorArtSubstage$.asObservable(); }

    private _priorArtCandidatesFound$ = new Subject<any>();
    get priorArtCandidatesFound$() { return this._priorArtCandidatesFound$.asObservable(); }

    private _priorArtCandidatesDiscovered$ = new Subject<any>();
    get priorArtCandidatesDiscovered$() { return this._priorArtCandidatesDiscovered$.asObservable(); }

    private _priorArtCandidateReady$ = new Subject<any>();
    get priorArtCandidateReady$() { return this._priorArtCandidateReady$.asObservable(); }

    private _priorArtCandidateScored$ = new Subject<any>();
    get priorArtCandidateScored$() { return this._priorArtCandidateScored$.asObservable(); }

    private _priorArtThreadComplete$ = new Subject<any>();
    get priorArtThreadComplete$() { return this._priorArtThreadComplete$.asObservable(); }

    constructor(private _authService: AuthService) {
        this._authSub = this._authService.authStatus$
            .pipe(filter(status => status.initialized))
            .subscribe(status => {
                if (status.state === AuthState.AUTHENTICATED) {
                    this._connect();
                } else {
                    this._disconnect();
                }
            });
    }

    ngOnDestroy(): void {
        this._authSub?.unsubscribe();
        this._disconnect();
    }

    emit(event: string, data?: any): void {
        this._socket?.emit(event, data);
    }

    joinProject(projectId: number | string): void {
        this.emit('join:project', projectId);
    }

    leaveProject(projectId: number | string): void {
        this.emit('leave:project', projectId);
    }

    private _connect(): void {
        if (this._socket?.connected) return;

        this._socket = io(environment.apiUrl, {
            withCredentials: true,
            transports: ['websocket', 'polling'],
        });

        this._socket.on('connect', () => {
            this._connected$.next(true);
        });

        this._socket.on('disconnect', () => {
            this._connected$.next(false);
        });

        // Notification events
        this._socket.on('notification', (data: Message) => {
            this._notification$.next(data);
        });

        // Future event listeners (no consumers yet, just forward to subjects)
        this._socket.on('stage:progress', (data: any) => {
            this._stageProgress$.next(data);
        });

        this._socket.on('stage:status', (data: any) => {
            this._stageStatus$.next(data);
        });

        this._socket.on('nav:update', (data: any) => {
            this._navUpdate$.next(data);
        });

        // Ideas streaming events
        this._socket.on('idea:generated', (data: any) => {
            this._ideaGenerated$.next(data);
        });

        this._socket.on('idea:assessed', (data: any) => {
            this._ideaAssessed$.next(data);
        });

        this._socket.on('idea:rejected', (data: any) => {
            this._ideaRejected$.next(data);
        });

        this._socket.on('ideas:batch-complete', (data: any) => {
            this._ideasBatchComplete$.next(data);
        });

        // Ideas property-level streaming events
        this._socket.on('idea:streaming-start', (data: any) => {
            console.log('[SOCKET] idea:streaming-start', data?.idea?.id?.substring(0, 8));
            this._ideaStreamingStart$.next(data);
        });

        this._socket.on('idea:streaming-update', (data: any) => {
            console.log('[SOCKET] idea:streaming-update', data?.ideaId?.substring(0, 8), Object.keys(data?.fields || {}).join(','));
            this._ideaStreamingUpdate$.next(data);
        });

        this._socket.on('idea:streaming-complete', (data: any) => {
            console.log('[SOCKET] idea:streaming-complete', data?.ideaId?.substring(0, 8));
            this._ideaStreamingComplete$.next(data);
        });

        // Deconstruction streaming events
        this._socket.on('decon:claim-start', (data: any) => {
            this._deconClaimStart$.next(data);
        });

        this._socket.on('decon:element-start', (data: any) => {
            this._deconElementStart$.next(data);
        });

        this._socket.on('decon:element-id', (data: any) => {
            this._deconElementId$.next(data);
        });

        this._socket.on('decon:element-text', (data: any) => {
            this._deconElementText$.next(data);
        });

        this._socket.on('decon:element-complete', (data: any) => {
            this._deconElementComplete$.next(data);
        });

        this._socket.on('decon:claim-complete', (data: any) => {
            this._deconClaimComplete$.next(data);
        });

        // Prior art search events
        this._socket.on('prior-art:substage', (data: any) => {
            this._priorArtSubstage$.next(data);
        });

        this._socket.on('prior-art:candidates-found', (data: any) => {
            this._priorArtCandidatesFound$.next(data);
        });

        this._socket.on('prior-art:candidates-discovered', (data: any) => {
            this._priorArtCandidatesDiscovered$.next(data);
        });

        this._socket.on('prior-art:candidate-ready', (data: any) => {
            this._priorArtCandidateReady$.next(data);
        });

        this._socket.on('prior-art:candidate-scored', (data: any) => {
            this._priorArtCandidateScored$.next(data);
        });

        this._socket.on('prior-art:thread-complete', (data: any) => {
            this._priorArtThreadComplete$.next(data);
        });
    }

    private _disconnect(): void {
        if (this._socket) {
            this._socket.disconnect();
            this._socket = null;
            this._connected$.next(false);
        }
    }
}
